/* eslint-disable prettier/prettier */
/* eslint-disable @next/next/no-img-element */
import NewButton from 'components/elements/NewButton'
import Stack from 'components/elements/Stack'
import DownloadButton from 'components/modules/SEO/DownloadBanner'
import _useTranslation from 'lib/hooks/_useTranslation'
import { Body1, ButtonText, Headline4, Headline5 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import DownloadQRCode from '../widgets/QRCode'
import LogoutIcon from 'assets/icons/logout.svg'
import RefreshIcon from 'assets/icons/refresh_action.svg'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import PreventAccessDialogPhone from 'assets/prevent-access/iphone.png'
import { KnowunityNumbers } from 'lib/constants/KnowunityNumbers'
import { useState } from 'react'

const Container = styled.div<{ isDarkTheme: boolean }>`
    padding: 25px 50px 25px 50px;
    display: grid;
    height: 650px;
    grid-template-columns: 1fr;
    justify-content: center;
    align-items: center;
    gap: 0px;
    width: fit-content;
    position: relative;
    @media (max-width: 800px) {
        gap: 10px;
        padding: 25px;
    }
    .button {
        width: 100px;
        height: 30px;
    }
    .texts {
        height: 100%;
        display: flex;
        flex-direction: column;
        gap: 30px;

        align-items: center;
        strong {
            font-family: var(--font-inter);
            color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.headlineDark)};
        }
    }
    .screenshot {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    .grid {
        width: 100%;
        gap: 50px;
        display: grid;

        grid-template-columns: 1fr 1fr;

        .qr {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 25px;
            ${({ isDarkTheme }) =>
                isDarkTheme
                    ? `:last-child > div {
                border: 4px solid ${theme.colors.white};
                border-radius: ${theme.borderRadius.large};
            }`
                    : ''}
        }
    }
    .phone {
        height: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
    .banner {
        width: 100%;
        padding: 15px;
        border: 1px solid ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.deepSeaBlue)};
        ${({ isDarkTheme }) => (isDarkTheme ? `background-color: ${theme.colors.backgroundTransparent};` : '')}
        border-radius: ${theme.borderRadius.normal};
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 10px;
    }
    .banner-texts {
        display: flex;
        gap: 5px;
        flex-direction: column;
        justify-content: center;
    }
    .buttons {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 7.5px;
    }
    .actions {
        overflow: visible;
        svg {
            fill: transparent;
            ${({ isDarkTheme }) =>
                isDarkTheme
                    ? css`
                          path {
                              stroke: ${theme.colors.grayScaleSecondary};
                          }
                      `
                    : ''}
        }
        display: flex;

        position: absolute;
        gap: 0;

        bottom: -30px;
        left: -15px;

        @media (max-height: 800px) {
            bottom: 25px;
            left: 0px;
        }
        button {
            position: relative;
            ${({ isDarkTheme }) => (isDarkTheme ? `background-color: ${theme.colors.transparent};` : '')}
            .tooltip {
                position: absolute;
                background-color: ${theme.colors.deepSeaBlue};
                border-radius: ${theme.borderRadius.large};
                padding: 10px 15px;
                white-space: nowrap;
                bottom: 55px;
                left: 5px;
                ::after {
                    content: '';
                    position: absolute;
                    bottom: -9px;
                    left: 12.5px;
                    width: 0px;
                    height: 0px;
                    border-left: 10px solid transparent;
                    border-right: 10px solid transparent;
                    border-top: 10px solid ${theme.colors.deepSeaBlue};
                }
            }
        }
    }
`

interface Props {
    onDownloadButtonClick: (type: 'android' | 'ios') => void
    refreshUser: () => void
    handleLogout: () => void
    loadingArea: string
    isDarkTheme: boolean
}

const PreventWebappAccessWhatsappSecurityBody = ({
    handleLogout,
    refreshUser,
    onDownloadButtonClick,
    loadingArea,
    isDarkTheme,
}: Props) => {
    const [wasRefreshTriggered, setWasRefreshTriggered] = useState(false)
    const { t: tCommon } = _useTranslation('common')
    const { t } = _useTranslation('app')
    const laptop = useMedia(deviceSize.lgHeight)

    const texts = [
        'app/preventWebappAccessSecurityStep1',
        'app/preventWebappAccessSecurityStep2',
        'app/preventWebappAccessSecurityStep3',
    ]

    const onRefreshClicked = () => {
        if (!wasRefreshTriggered) {
            setWasRefreshTriggered(true)
        }

        refreshUser()
    }

    return (
        <Container isDarkTheme={isDarkTheme}>
            <div className="banner">
                <div className="phone">
                    <img
                        src={PreventAccessDialogPhone.src}
                        width={PreventAccessDialogPhone.width}
                        height={PreventAccessDialogPhone.height}
                        alt="logo"
                    />
                </div>
                <div className="banner-texts">
                    <Headline5
                        fontFamily="var(--font-open-sans)"
                        fontWeight={500}
                        color={isDarkTheme ? theme.colors.white : '#000'}
                    >
                        {t('app/preventAccessDialogWhatsappBannerTitle')}
                    </Headline5>
                    <Body1
                        fontFamily="var(--font-open-sans)"
                        fontWeight={400}
                        color={isDarkTheme ? theme.colors.grayScaleSecondary : '#000'}
                    >
                        {t('app/preventAccessDialogWhatsappBannerSubtitle', {
                            userCount: KnowunityNumbers.downloadMillionCount,
                        })}
                    </Body1>
                </div>
                <div className="buttons">
                    <DownloadButton
                        type="android"
                        referrerScreen="prevent-webapp-access"
                        isDark
                        backgroundColor={isDarkTheme ? theme.colors.headlineDark : theme.colors.deepSeaBlue}
                        onClick={() => onDownloadButtonClick('android')}
                    />
                    <DownloadButton
                        type="ios"
                        referrerScreen="prevent-webapp-access"
                        isDark
                        backgroundColor={isDarkTheme ? theme.colors.headlineDark : theme.colors.deepSeaBlue}
                        onClick={() => onDownloadButtonClick('ios')}
                    />
                </div>
            </div>
            <div className="texts">
                <Headline4
                    color={isDarkTheme ? theme.colors.white : theme.colors.deepSeaBlue}
                    fontFamily="var(--font-open-sans)"
                    fontWeight={600}
                    textAlign="center"
                >
                    {t('app/preventWebappAccessSecurityTitle')}
                </Headline4>

                <div className="grid">
                    <div className="qr">
                        <Stack gutter={40}>
                            {texts.map((copy, i) => (
                                <Headline5
                                    key={`explanation-text-${i}`}
                                    color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.headlineDark}
                                    fontFamily="var(--font-open-sans)"
                                    fontWeight={400}
                                >
                                    {`${++i}. ${t(`${copy}`)}`}
                                </Headline5>
                            ))}
                        </Stack>
                    </div>
                    <div className="qr">
                        <DownloadQRCode
                            referrerScreen="prevent-webapp-access-qr-code"
                            codeColor={isDarkTheme ? theme.colors.headlineDark : theme.colors.subtitleBlack}
                            size={laptop ? 225 : 300}
                        />
                    </div>
                </div>
            </div>
            <div className="actions">
                <NewButton
                    color={isDarkTheme ? theme.colors.transparent : theme.colors.white}
                    borderRadius={theme.borderRadius.normal}
                    type={isDarkTheme ? 'primary' : 'secondary'}
                    icon={<RefreshIcon />}
                    loadingArea={loadingArea}
                    onClick={onRefreshClicked}
                >
                    {wasRefreshTriggered ? (
                        <div className="tooltip">
                            <Body1
                                fontFamily="var(--font-inter)"
                                fontWeight={400}
                                color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.black}
                                fontSize={14}
                            >
                                {t('app/preventWebappAccessRefreshExplanationMessage')}
                            </Body1>
                        </div>
                    ) : null}
                    <ButtonText
                        color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.black}
                        textAlign="center"
                        fontFamily="var(--font-open-sans)"
                        fontWeight={400}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {t('app/preventWebappAccessRefreshButtonText')}
                    </ButtonText>
                </NewButton>

                <NewButton
                    color={isDarkTheme ? theme.colors.transparent : theme.colors.white}
                    borderRadius={theme.borderRadius.normal}
                    icon={<LogoutIcon />}
                    type={isDarkTheme ? 'primary' : 'secondary'}
                    onClick={handleLogout}
                >
                    <ButtonText
                        color={isDarkTheme ? theme.colors.grayScaleSecondary : theme.colors.black}
                        textAlign="center"
                        fontFamily="var(--font-open-sans)"
                        fontWeight={400}
                    >
                        {tCommon('common/navBarLogoutLink')}
                    </ButtonText>
                </NewButton>
            </div>
        </Container>
    )
}

export default PreventWebappAccessWhatsappSecurityBody
